/**
 * Which statistical test? A decision tree over the questions a statistician asks: what is
 * the goal, what kind of outcome, how many groups, are the measurements related, are
 * the parametric assumptions reasonable.
 *
 * The tree is plain data and two small functions, so every leaf can be tested and every
 * answer can explain itself. It recommends only tests the site has a calculator for;
 * a goal outside that set (a count or ordinal outcome to be modelled, say) is said to be
 * outside it rather than forced onto the nearest test.
 */

export type QuestionId =
  | "goal"
  | "outcome"
  | "groups"
  | "design"
  | "assumptions"
  | "sigma"
  | "factors"
  | "categories"
  | "expected"
  | "relateKinds"
  | "relateAssumptions"
  | "predictOutcome"
  | "predictors";

export type Answers = Partial<Record<QuestionId, string>>;

/** The options each question offers, in display order. */
export const QUESTION_OPTIONS: Record<QuestionId, readonly string[]> = {
  goal: ["compare", "relate", "predict"],
  outcome: ["numeric", "ordinal", "categorical"],
  groups: ["one", "two", "many"],
  design: ["independent", "paired"],
  assumptions: ["met", "notmet"],
  sigma: ["unknown", "known"],
  factors: ["one", "two"],
  categories: ["two", "many"],
  expected: ["ok", "small"],
  relateKinds: ["numeric", "ordinal", "categorical"],
  relateAssumptions: ["met", "notmet"],
  predictOutcome: ["numeric", "binary", "other"],
  predictors: ["one", "several"],
};

export type TestId =
  | "ttest"
  | "ztest"
  | "sign"
  | "wilcoxon"
  | "mannwhitney"
  | "anova"
  | "twoway"
  | "tukey"
  | "kruskal"
  | "rmanova"
  | "friedman"
  | "binomial"
  | "chisq"
  | "fisher"
  | "twoprop"
  | "mcnemar"
  | "cochran"
  | "pearson"
  | "spearman"
  | "kendall"
  | "linear"
  | "multiple"
  | "logistic"
  | "shapiro"
  | "effect"
  | "apa"
  | "power";

/** The calculator page for every test the selector can name. */
export const TEST_SLUGS: Record<TestId, string> = {
  ttest: "t-test-calculator",
  ztest: "z-test-calculator",
  sign: "sign-test-calculator",
  wilcoxon: "wilcoxon-signed-rank-test-calculator",
  mannwhitney: "mann-whitney-u-test-calculator",
  anova: "anova-calculator",
  twoway: "two-way-anova-calculator",
  tukey: "tukey-hsd-calculator",
  kruskal: "kruskal-wallis-test-calculator",
  rmanova: "repeated-measures-anova-calculator",
  friedman: "friedman-test-calculator",
  binomial: "binomial-test-calculator",
  chisq: "chi-square-calculator",
  fisher: "fisher-exact-test-calculator",
  twoprop: "two-proportion-z-test-calculator",
  mcnemar: "mcnemar-test-calculator",
  cochran: "cochrans-q-test-calculator",
  pearson: "correlation-calculator",
  spearman: "spearman-correlation-calculator",
  kendall: "kendall-tau-calculator",
  linear: "linear-regression-calculator",
  multiple: "multiple-regression-calculator",
  logistic: "logistic-regression-calculator",
  shapiro: "shapiro-wilk-test-calculator",
  effect: "effect-size-calculator",
  apa: "apa-statistics-formatter",
  power: "power-analysis-calculator",
};

/* -------------------------------------------------------------- the path */

function numericCompareExtras(a: Answers): QuestionId[] {
  const extras: QuestionId[] = ["assumptions"];
  if (a.assumptions !== "met") {
    return extras;
  }
  if (a.groups === "one") {
    extras.push("sigma");
  }
  if (a.groups === "many" && a.design === "independent") {
    extras.push("factors");
  }
  return extras;
}

function categoricalCompareExtras(a: Answers): QuestionId[] {
  if (a.groups === "one") {
    return ["categories"];
  }
  if (a.groups === "two" && a.design === "independent") {
    return ["expected"];
  }
  return [];
}

function compareQuestions(a: Answers): QuestionId[] {
  const list: QuestionId[] = ["outcome", "groups"];
  if (a.groups === "two" || a.groups === "many") {
    list.push("design");
  }
  if (a.outcome === "numeric") {
    list.push(...numericCompareExtras(a));
  }
  if (a.outcome === "categorical") {
    list.push(...categoricalCompareExtras(a));
  }
  return list;
}

function relateQuestions(a: Answers): QuestionId[] {
  const list: QuestionId[] = ["relateKinds"];
  if (a.relateKinds === "numeric") {
    list.push("relateAssumptions");
  }
  if (a.relateKinds === "categorical") {
    list.push("expected");
  }
  return list;
}

function predictQuestions(a: Answers): QuestionId[] {
  const list: QuestionId[] = ["predictOutcome"];
  if (a.predictOutcome === "numeric") {
    list.push("predictors");
  }
  return list;
}

/** Every question that applies given the answers so far, in the order they are asked. */
export function applicableQuestions(a: Answers): QuestionId[] {
  const list: QuestionId[] = ["goal"];
  if (a.goal === "compare") {
    list.push(...compareQuestions(a));
  } else if (a.goal === "relate") {
    list.push(...relateQuestions(a));
  } else if (a.goal === "predict") {
    list.push(...predictQuestions(a));
  }
  return list;
}

/** The questions to show: those answered, then the first one still open. */
export function visibleQuestions(a: Answers): {
  shown: QuestionId[];
  complete: boolean;
} {
  const shown: QuestionId[] = [];
  for (const question of applicableQuestions(a)) {
    shown.push(question);
    if (!isAnswered(a, question)) {
      return { shown, complete: false };
    }
  }
  return { shown, complete: true };
}

function isAnswered(a: Answers, question: QuestionId): boolean {
  const value = a[question];
  return value !== undefined && QUESTION_OPTIONS[question].includes(value);
}

/* -------------------------------------------------------- recommendations */

export interface Recommendation {
  /** Null when the goal is outside the tests the site has. */
  primary: TestId | null;
  alternatives: TestId[];
  followUps: TestId[];
  checks: TestId[];
  /** The answers that led here, in order, for the "why" list. */
  reasons: { question: QuestionId; answer: string }[];
}

interface Leaf {
  primary: TestId | null;
  alternatives?: TestId[];
  followUps?: TestId[];
  checks?: TestId[];
}

function oneGroupNumeric(a: Answers): Leaf {
  if (a.assumptions !== "met") {
    return { primary: "sign", alternatives: ["wilcoxon"] };
  }
  if (a.sigma === "known") {
    return { primary: "ztest", alternatives: ["ttest"], checks: ["shapiro"] };
  }
  return { primary: "ttest", alternatives: ["sign"], checks: ["shapiro"] };
}

function twoGroupNumeric(a: Answers): Leaf {
  const paired = a.design === "paired";
  if (a.assumptions === "met") {
    return {
      primary: "ttest",
      alternatives: [paired ? "wilcoxon" : "mannwhitney"],
      checks: ["shapiro"],
    };
  }
  return paired
    ? { primary: "wilcoxon", alternatives: ["sign", "ttest"] }
    : { primary: "mannwhitney", alternatives: ["ttest"] };
}

function manyGroupNumeric(a: Answers): Leaf {
  const paired = a.design === "paired";
  if (a.assumptions !== "met") {
    return paired
      ? { primary: "friedman" }
      : { primary: "kruskal", alternatives: ["anova"] };
  }
  if (paired) {
    return {
      primary: "rmanova",
      alternatives: ["friedman"],
      checks: ["shapiro"],
    };
  }
  return a.factors === "two"
    ? { primary: "twoway", alternatives: ["kruskal"], checks: ["shapiro"] }
    : {
        primary: "anova",
        alternatives: ["kruskal"],
        followUps: ["tukey"],
        checks: ["shapiro"],
      };
}

function compareNumeric(a: Answers): Leaf {
  if (a.groups === "one") {
    return oneGroupNumeric(a);
  }
  return a.groups === "two" ? twoGroupNumeric(a) : manyGroupNumeric(a);
}

function compareCategorical(a: Answers): Leaf {
  const paired = a.design === "paired";
  if (a.groups === "one") {
    return a.categories === "two"
      ? { primary: "binomial", alternatives: ["chisq"] }
      : { primary: "chisq" };
  }
  if (paired) {
    return a.groups === "two"
      ? { primary: "mcnemar", alternatives: ["sign"] }
      : { primary: "cochran" };
  }
  if (a.groups === "two") {
    return a.expected === "small"
      ? { primary: "fisher", alternatives: ["chisq"] }
      : { primary: "chisq", alternatives: ["twoprop", "fisher"] };
  }
  return { primary: "chisq" };
}

function compareLeaf(a: Answers): Leaf {
  if (a.outcome === "numeric") {
    return compareNumeric(a);
  }
  if (a.outcome === "ordinal") {
    // Ranks: the same road as numeric data whose assumptions fail.
    return compareNumeric({ ...a, assumptions: "notmet" });
  }
  return compareCategorical(a);
}

function relateLeaf(a: Answers): Leaf {
  if (a.relateKinds === "numeric") {
    return a.relateAssumptions === "met"
      ? { primary: "pearson", alternatives: ["spearman"], checks: ["shapiro"] }
      : { primary: "spearman", alternatives: ["kendall"] };
  }
  if (a.relateKinds === "ordinal") {
    return { primary: "spearman", alternatives: ["kendall"] };
  }
  return a.expected === "small"
    ? { primary: "fisher", alternatives: ["chisq"] }
    : { primary: "chisq", alternatives: ["fisher"] };
}

function predictLeaf(a: Answers): Leaf {
  if (a.predictOutcome === "numeric") {
    return a.predictors === "several"
      ? { primary: "multiple" }
      : { primary: "linear", alternatives: ["multiple"] };
  }
  if (a.predictOutcome === "binary") {
    return { primary: "logistic" };
  }
  return { primary: null };
}

/** The recommendation once every applicable question is answered; null before that. */
export function recommend(a: Answers): Recommendation | null {
  const { shown, complete } = visibleQuestions(a);
  if (!complete) {
    return null;
  }
  let leaf: Leaf;
  if (a.goal === "compare") {
    leaf = compareLeaf(a);
  } else if (a.goal === "relate") {
    leaf = relateLeaf(a);
  } else {
    leaf = predictLeaf(a);
  }
  const common: TestId[] = leaf.primary === null ? [] : ["effect", "apa"];
  return {
    primary: leaf.primary,
    alternatives: leaf.alternatives ?? [],
    followUps: [...(leaf.followUps ?? []), ...common],
    checks: leaf.checks ?? [],
    reasons: shown.map((question) => ({
      question,
      answer: a[question] as string,
    })),
  };
}
